// src/lib/pharmacyFirst/pathways/aht/aht-wt-foundayo-transfer.pathway.ts
//
// AHT Weight Management — Orforglipron (Foundayo®) capsule-to-tablet Transfer Supply Pathway
// Source: AHT Foundayo PGD v2.1 (Drug Details; Dosage & Frequency > Transfer from orforglipron hard capsules).
//
// QUANTITIES ARE PRE-VERIFIED LOOKUPS. DO NOT CALCULATE.
// The tablet strength is read from the PGD's capsule-to-tablet table (SmPC section 4.2, Table 2), never calculated.
// Capsules are never a supply option: only film-coated tablets are supplied.

import type { Pathway } from "../../types/pathway.types";
import { buildFoundayoSupplyDownstream, foundayoRedirectOutcome, FDY_OUT_CANNOT_SUPPLY } from "./_shared/foundayo-supply-downstream";
import { FDY_TRANSFER_MAX_DAYS_SINCE_LAST_CAPSULE, daysSinceIsoDate } from "./_shared/foundayo-route-helpers";

const Q_SOURCE_KNOWN = "fdy-trans-q-source-known";
const Q_SOURCE_TEXT = "fdy-trans-q-source-text";
const Q_CAPSULE_STRENGTH = "fdy-trans-q-capsule-strength";
const Q_CAP = "fdy-trans-q-dose-cap";
const R_CAP = "fdy-trans-cap-route";
const Q_STARTED_DATE = "fdy-trans-q-capsule-dose-start-date";
const R_LAST_DOSE = "fdy-trans-last-capsule-route";

const OUT_LEAD_PHARMACIST = "FDY_OUT_REFER_LEAD_PHARMACIST";
const OUT_USE_REINITIATION = "FDY_OUT_USE_REINITIATION_ROUTE";

// SmPC section 4.2, Table 2 as set out in the PGD. Option ids are the tablet dose step ids on the PGD.
const CAPSULE_TO_TABLET: { capsuleMg: number; tabletStepId: string; tabletMg: number }[] = [
  { capsuleMg: 1, tabletStepId: "fdy-0_8mg", tabletMg: 0.8 },
  { capsuleMg: 3, tabletStepId: "fdy-2_5mg", tabletMg: 2.5 },
  { capsuleMg: 6, tabletStepId: "fdy-5_5mg", tabletMg: 5.5 },
  { capsuleMg: 12, tabletStepId: "fdy-9mg", tabletMg: 9 },
  { capsuleMg: 24, tabletStepId: "fdy-14_5mg", tabletMg: 14.5 },
  { capsuleMg: 36, tabletStepId: "fdy-17_2mg", tabletMg: 17.2 },
];

// Tablet steps above the 9 mg maximum for a strong CYP3A4 inhibitor or clinical OATP1B inhibitor.
const STEPS_ABOVE_9MG = ["fdy-14_5mg", "fdy-17_2mg"];

const downstream = buildFoundayoSupplyDownstream({
  idPrefix: "fdy-trans",
  attestHeading: "Confirm transfer supply of Foundayo® tablets (replacing capsules)",
  attestLead: [
    "Supplied one intact 30-tablet pack of the single tablet strength selected above, never a split 90-tablet pack and never more than one month",
    "Confirmed the tablet imprint (G1 to G6) matches the strength selected, and that the stock came from an MHRA registered wholesaler with the supplier recorded",
    "Confirmed the patient continues the existing titration: no restart at 0.8 mg, no re-titration, no treatment-free interval; the first tablet is taken the day after the last capsule, at the usual time",
    "Told the patient the capsules and tablets are not interchangeable mg for mg and that capsules must not be taken once tablets are started",
    "Recorded the date the current capsule dose was started: the next dose increase is permitted no sooner than 30 days after that date, not the date of transfer",
    "Recorded the source of the capsules; the evidence seen, baseline BMI and last dose date were recorded in the shared assessment",
  ],
  supplyTitle: "Transfer supply complete — Foundayo® tablets (from capsules)",
  supplyReason:
    "Capsule source and evidence recorded, equivalent tablet strength supplied from the PGD conversion table, no exclusion criteria triggered, and supply attestation confirmed.",
  extraSafetyNetting: [
    "Do not take capsules and tablets together. The next dose increase is permitted no sooner than 30 days after the date the capsule dose was started.",
  ],
});

export const ahtWtFoundayoTransferPathway: Pathway = {
  id: "aht-wt-foundayo-transfer",
  name: "Orforglipron (Foundayo®) — Transfer from capsules to tablets",
  population: "Adults aged 18–84 already established on orforglipron hard capsules",
  pgdRefs: ["pgd-aht-wt-foundayo"],
  demographicConfiguration: { bannerFields: [] },
  entryNode: Q_SOURCE_KNOWN,

  nodes: [
    {
      id: Q_SOURCE_KNOWN,
      type: "yes_no",
      text: "Is the source of the patient's orforglipron capsules known and documented?",
      hint: "If the supplier cannot be identified, the case goes to the Lead Pharmacist. Do not supply tablets without a clear source.",
      answerPolarity: "yes-positive",
      yes: Q_SOURCE_TEXT,
      no: { outcomeId: OUT_LEAD_PHARMACIST },
    },

    {
      id: Q_SOURCE_TEXT,
      type: "free_text",
      text: "Record the source of the capsules (previous supplier or prescriber).",
      required: true,
      minLength: 3,
      storesTo: "fdyTransferCapsuleSource",
      next: Q_CAPSULE_STRENGTH,
    },

    {
      id: Q_CAPSULE_STRENGTH,
      type: "single_select",
      text: "Which orforglipron capsule strength is the patient taking? The equivalent tablet strength is shown.",
      hint: "The conversion is fixed by the PGD table. Do not restart at 0.8 mg and do not re-titrate.",
      options: CAPSULE_TO_TABLET.map((row) => ({
        id: row.tabletStepId,
        label: `${row.capsuleMg} mg capsule — supply ${row.tabletMg} mg tablets`,
      })),
      routing: Object.fromEntries(CAPSULE_TO_TABLET.map((row) => [row.tabletStepId, Q_CAP])),
      storesTo: "selectedDoseStep",
      required: true,
    },

    {
      id: Q_CAP,
      type: "yes_no",
      text: "Is the patient taking a strong CYP3A4 inhibitor (ketoconazole, clarithromycin, itraconazole) or a clinical OATP1B inhibitor (ciclosporin)?",
      hint: "The maximum dose with either is 9 mg once daily. This is asked afresh, not carried over.",
      answerPolarity: "neutral",
      yes: R_CAP,
      no: Q_STARTED_DATE,
    },

    {
      id: R_CAP,
      type: "condition_route",
      conditions: [
        {
          evaluate: (ctx) => STEPS_ABOVE_9MG.includes(String(ctx.context["selectedDoseStep"])),
          then: { outcomeId: OUT_LEAD_PHARMACIST },
        },
      ],
      default: Q_STARTED_DATE,
    },

    {
      id: Q_STARTED_DATE,
      type: "date_input",
      text: "Date the patient started their current capsule dose",
      hint: "The next dose increase is permitted no sooner than 30 days after this date.",
      storesTo: "fdyTransferCapsuleDoseStartDate",
      max: 0,
      next: R_LAST_DOSE,
    },

    {
      id: R_LAST_DOSE,
      type: "condition_route",
      conditions: [
        {
          // Fail safe: a missing, invalid or future date gives NaN, which is not within the limit.
          evaluate: (ctx) => !(daysSinceIsoDate(ctx.context["sharedTransferLastDoseDate"]) <= FDY_TRANSFER_MAX_DAYS_SINCE_LAST_CAPSULE),
          then: { outcomeId: OUT_USE_REINITIATION },
        },
      ],
      default: downstream.entryNodeId,
    },

    ...downstream.nodes,
  ],

  outcomes: {
    ...downstream.outcomes,
    [OUT_LEAD_PHARMACIST]: foundayoRedirectOutcome(
      OUT_LEAD_PHARMACIST,
      "Refer to the Lead Pharmacist — Foundayo® transfer",
      "The capsule source is unknown, or the equivalent tablet strength is above the 9 mg maximum that applies with a strong CYP3A4 inhibitor or OATP1B inhibitor. A clinical decision is needed before any supply.",
      [
        "Do not supply tablets until the Lead Pharmacist has reviewed the case.",
        "Record the capsule strength, the interacting medicine (if any) and the reason for referral.",
      ],
      "refer_routine",
    ),
    [OUT_USE_REINITIATION]: foundayoRedirectOutcome(
      OUT_USE_REINITIATION,
      "Use the returning patient route — Foundayo®",
      "The last capsule dose was more than 30 days ago, so this is not a continuous transfer. The gap rules for restarting apply.",
      [
        "Start a Returning patient (re-initiation) consultation.",
        "A gap of 30 days or less resumes the last tolerated dose; a longer gap restarts at 0.8 mg.",
      ],
    ),
  },
};
